import type {SagaIterator} from 'redux-saga';
import {replace} from 'connected-react-router';
import Store from 'store';

import {SEARCH_RESULTS} from 'constants/actions';
import type {GetSearchResultsSagaParamsType} from 'constants/flow/flowTypes';
import {NEUTRAL_CATEGORY_ID} from 'constants/category';

import {takeEvery, call, put, putResolve, cancel, select, getContext} from 'redux-saga/effects';
import {getFilters, setActiveFilters, fetchCategoryItems} from 'actions/category/category';
import type {ActiveFiltersType} from 'reducers/category/activeFilters/activeFiltersType';
import {getActiveFiltersFromUrl} from 'libs/category/category';
import {getFilters as getFiltersSelector} from 'selectors/filters/filters.js';
import {getUrlParameter} from 'libs/url';
import {POST_MESSAGE_TYPES, postMessage} from 'libs/postMessage/postMessage';
import {fetchFaces} from '../../features/tryOn/sagas/tryOn';

export const SEARCH_PROVIDER_ALGOLIA = 'algolia';

/**
 * Provide algolia search params
 * @param {string | null} query - query string
 * @returns {Object} - resulting object
 */
export const getAlgoliaSearchParams = (
    query: string | null,
): {q: string, search_provider?: string} | {} => {
    if (!query) return {};

    return {search_provider: SEARCH_PROVIDER_ALGOLIA, q: query};
};

/**
 * Performs redirect to search results page
 * @param {string} query - Received query parameter
 */
export function* redirectToSearchResults(query: string): SagaIterator {
    const history = yield getContext('history');
    yield call(history.push, `/salesperson/result?q=${query}`);
}

/**
 * Performs redirect to product page
 * @param {string} url - Received first simple product url
 */
export function* redirectToProductPage(url: string): SagaIterator {
    const pushAction = yield call(replace, url);
    yield putResolve(pushAction);
}

/**
 * Performs redirect to custom page (also adds search parameter with received query string)
 * @param {string} url - Received url for redirect
 */
export function* redirectToCustomPage(url: string): SagaIterator {
    const urlParser = document.createElement('a'); // hack for correct url string parsing
    urlParser.href = url;
    const replaceAction = yield call(replace, `${urlParser.pathname}${urlParser.search}`);
    yield putResolve(replaceAction);
}

/**
 * Saves received IDs list in to browser store as string
 * @param {array<string>} ids - Received ids list
 */
export function* saveReceivedIDsSaga(ids: array<string>): SagaIterator {
    yield call([Store, 'set'], 'SEARCH_RESULTS_IDS_LIST', ids.join(','));
}

/**
 * Picks available filters from URL search parameters
 * @returns {SagaIterator<SearchResultsType>} - list of available filters
 */
export function* pickFiltersFromUrl(): SagaIterator<SearchResultsType> {
    const availableFilters = yield select(getFiltersSelector);
    const filters = yield call(getActiveFiltersFromUrl, availableFilters.items);

    return filters;
}

/**
 * Initializes basic category data: fetches items, filters, applies filters from URL
 * @param {ActiveFiltersType} filters - list of active filters
 * @param {string} query - query url string
 * @returns {SagaIterator} - category items
 */
export function* fetchSearchCategoryItems(filters: ActiveFiltersType, query: string): SagaIterator {
    const sorting = yield call(getUrlParameter, 'order');
    const [order, dir] = sorting.split(',');
    const additionalParams = yield call(getAlgoliaSearchParams, query);

    const {action} = yield putResolve(
        // @ts-ignore
        fetchCategoryItems(NEUTRAL_CATEGORY_ID, filters, {order, dir, additionalParams}), // page field is missing in the last arg
    );

    return action;
}

/**
 * Saga which receives search results by received query
 * @param {string} query - Received search query
 * @returns {SagaIterator<SearchResultsType> } - Search results data
 */
export function* getSearchResults(query: string): SagaIterator<SearchResultsType> {
    const filtersResponse = yield putResolve(getFilters(NEUTRAL_CATEGORY_ID, query));

    if (filtersResponse?.value?.data?.redirectUrl) {
        return filtersResponse.value.data;
    }

    const filters = yield call(pickFiltersFromUrl);

    yield put(setActiveFilters(filters));

    yield call(fetchFaces);

    const {
        payload: {data},
    } = yield call(fetchSearchCategoryItems, filters, query);
    return data;
}

/**
 * Receive search results on success
 * @param {ActionWithPayloadType<GetSearchResultsSagaParamsType>} action - Received action
 */
export function* receiveSearchResultsSuccess(
    action: ActionWithPayloadType<GetSearchResultsSagaParamsType>,
): SagaIterator {
    const {redirectUrl = null, items} = yield call(getSearchResults, action.payload.query);

    if (redirectUrl) {
        yield call(redirectToCustomPage, redirectUrl);
        yield cancel();
    }

    if (items && items.length === 1) {
        yield call(redirectToProductPage, items[0].children[0].url);
        yield cancel();
    }

    if (!action.payload.isReload) yield call(redirectToSearchResults, action.payload.query);
}

/**
 * Gets search results by received search query
 * @param {ActionType<GetSearchResultsSagaParamsType>} action - Received action
 */
export function* receiveSearchResults(
    action: ActionType<GetSearchResultsSagaParamsType>,
): SagaIterator {
    try {
        yield call(receiveSearchResultsSuccess, action);
    } catch (e) {
        yield call(postMessage, {type: POST_MESSAGE_TYPES.NAVIGATION, data: 'SHOP'});
    }
}

/**
 * Begin of search results saga
 */
export default function* searchResultsSaga(): SagaIterator {
    yield takeEvery(SEARCH_RESULTS.START_GET_SEARCH_RESULTS_SAGA, receiveSearchResults);
}
